import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  AskRequest,
  AskRequestCreateType,
  AskRequestStatus,
  CreateAskTransaction,
  LinkTypes,
} from '@hone-automation/common';
import { PendingFlagData } from 'hooks/useFlagCreationStore';
import { UIReportTransaction } from 'types/askcustomer';
import { useLocationsStore } from 'hooks/useLocationsStore';
import { useActiveReportStore } from 'hooks/useActiveReportStore';
import { useParams } from 'react-router-dom';
import { useQueueRequestsQuery } from 'hooks/useQueueRequestsQuery';
import {
  useCreateQueueRequestMutation,
  useCreateQueueCommentMutation,
  useReopenQueueRequestMutation,
} from 'hooks/queue';
import { transformAskRequestVisibility } from '../../Queue/utils/askRequestUtils';
import { ConversationPanel } from '../../KSQ2/Panels/ConversationPanel';
import { AskRequestQueueComment } from '../../KSQ2/Cards/MessagesCard/messagesCardFactory';
import { useQueryState } from 'hooks/useQueryState';
import { IconBack } from 'components/Icons';
import { shallow } from 'zustand/shallow';
import { useQueueContext } from '../../Queue/QueueContext';

interface FlagCreationPanelProps {
  pendingFlag: PendingFlagData;
  reportId?: string;
  onBack?: () => void;
  onDone: () => void;
}

const FlagCreationPanel: React.FC<FlagCreationPanelProps> = ({ pendingFlag, reportId, onBack, onDone }) => {
  const { refetch, isRefetching } = useQueueRequestsQuery();
  const { allLocations, currentLocation } = useLocationsStore(state => ({
    allLocations: state.allLocations,
    currentLocation: state.currentLocation,
  }));
  const { reportPayload, otherReportPayload } = useActiveReportStore(
    state => ({
      reportPayload: state.reportPayload,
      otherReportPayload: state.otherReportPayload,
    }),
    shallow
  );
  const { locationId: urlLocationId } = useParams();
  const [activeTab] = useQueryState<AskRequestStatus>('activeTab', 'Open');
  const { dispatch } = useQueueContext();

  // The transaction being flagged — always provided from TransactionsTable
  const [transaction, setTransaction] = useState<UIReportTransaction | undefined>(pendingFlag.transaction);

  const currentLocationId = pendingFlag.resolvedLocationId || urlLocationId;

  // Resolve location name for multi-location display
  const resolvedLocationName = useMemo(() => {
    if (!pendingFlag.resolvedLocationId) return null;
    if (currentLocation?.id === pendingFlag.resolvedLocationId) return currentLocation.name;
    const location = allLocations.find(loc => loc.id === pendingFlag.resolvedLocationId);
    return location?.name || null;
  }, [pendingFlag.resolvedLocationId, allLocations, currentLocation]);

  // If existingFlagId is set, auto-select in queue and exit flag creation
  useEffect(() => {
    if (pendingFlag.existingFlagId) {
      refetch().then(({ data: requests }) => {
        const existingRequest = requests?.find(r => r.id === pendingFlag.existingFlagId);
        if (existingRequest) {
          dispatch({ type: 'SET_SELECTED_ITEM', payload: existingRequest });
          onDone();
        }
      });
    }
  }, [pendingFlag.existingFlagId, refetch, dispatch, onDone]);

  // The selected transaction for ConversationPanel — either a real transaction or a cell-level stub
  const selectedTransaction = useMemo(() => {
    if (transaction) return transaction;
    // Cell-level flag with no specific transaction
    return {
      amount: pendingFlag.cellAmount,
      txnDate: pendingFlag.cellDate.start,
    } as UIReportTransaction;
  }, [transaction, pendingFlag]);

  // Build modalState for ConversationPanel compatibility
  const modalState = useMemo(
    () => ({
      modalOpen: true,
      audit: pendingFlag.audit,
      dataRows: pendingFlag.dataRows,
      date: pendingFlag.cellDate,
      title: pendingFlag.cellTitle,
      parent: pendingFlag.parentTitle,
      total: pendingFlag.cellAmount,
      loading: false,
      hasDataRowsOrAudit: pendingFlag.dataRows.length > 0,
      resolvedLocationId: pendingFlag.resolvedLocationId,
      reportId: pendingFlag.reportId,
    }),
    [pendingFlag]
  );

  // Mutation callbacks
  const { mutate: createQueueRequestMutation } = useCreateQueueRequestMutation(
    currentLocationId!,
    (trx: AskRequest) => {
      if (trx) {
        refetch().then(() => {
          dispatch({ type: 'SET_SELECTED_ITEM', payload: trx });
          onDone();
        });
      }
    }
  );

  const { mutate: createCommentMutation } = useCreateQueueCommentMutation(
    selectedTransaction?.flagged?.status as AskRequestStatus,
    currentLocationId!,
    (trx: AskRequest | undefined) => {
      if (trx) {
        setTransaction(prev => (prev ? ({ ...prev, flagged: trx } as UIReportTransaction) : prev));
      }
      refetch();
    }
  );

  const postReopenFlaggedTransaction = useReopenQueueRequestMutation(
    activeTab,
    currentLocationId!,
    undefined,
    (trx: AskRequest | undefined) => {
      if (trx && selectedTransaction) {
        setTransaction({ ...selectedTransaction, flagged: trx } as UIReportTransaction);
      }
      refetch();
    }
  );

  const isClosedTrx =
    selectedTransaction?.flagged?.status === 'Closed' ||
    (selectedTransaction?.flagged?.status as any) === 'Categorized';

  const canReopen = Boolean(
    selectedTransaction && selectedTransaction.flagged && selectedTransaction.flagged.canReopen && isClosedTrx
  );

  const handleComposerCallback = useCallback(
    (formData: FormData, createRequestType: AskRequestCreateType) => {
      if (!currentLocationId) return;

      // Reopen flow
      if (isClosedTrx && canReopen && selectedTransaction?.flagged) {
        postReopenFlaggedTransaction.mutate({
          trxId: selectedTransaction.flagged.id,
          comment: formData.get('comment') as string,
        });
        return;
      }

      // Comment flow
      if (selectedTransaction?.flagged) {
        const visibility = transformAskRequestVisibility(createRequestType);
        formData.set('visibility', visibility);
        formData.delete('createAskRequestType');
        createCommentMutation(formData);
        return;
      }

      // Create new request flow
      const numProperties = selectedTransaction ? Object.keys(selectedTransaction).length : 0;
      const trx: CreateAskTransaction | undefined =
        numProperties > 3 && selectedTransaction
          ? {
              memo: selectedTransaction.description,
              name: selectedTransaction.name,
              externalTransactionType: String(selectedTransaction.type),
              externalNum: selectedTransaction.num,
              externalId: String(selectedTransaction.eventId),
            }
          : undefined;

      formData.append('reportRequest', JSON.stringify(reportPayload || otherReportPayload));
      formData.append('type', numProperties > 3 ? 'Ask Bookkeeper' : 'Flagged Cell');
      formData.append('reportType', LinkTypes.Location_Report);
      formData.append('identifier', pendingFlag.cellTitle);
      formData.append('period', selectedTransaction?.txnDate || '');
      formData.append('flaggedAmount', String(selectedTransaction?.amount || 0));
      formData.append('askTransaction', JSON.stringify(trx));
      formData.append('createType', createRequestType);

      createQueueRequestMutation(formData as any);
    },
    [
      currentLocationId,
      isClosedTrx,
      canReopen,
      selectedTransaction,
      postReopenFlaggedTransaction,
      createCommentMutation,
      createQueueRequestMutation,
      reportPayload,
      otherReportPayload,
      pendingFlag.cellTitle,
    ]
  );

  const handleTransactionUpdate = useCallback(
    (updatedTransaction: UIReportTransaction) => {
      setTransaction(updatedTransaction);
      refetch();
    },
    [refetch]
  );

  return (
    <div className="flag-creation-panel">
      <div className="flag-creation-panel__header">
        <button type="button" className="flag-creation-panel__back" onClick={onBack || onDone}>
          <IconBack />
        </button>
        <span className="flag-creation-panel__title">
          FLAG
          {resolvedLocationName && <span className="flag-creation-panel__location">({resolvedLocationName})</span>}
        </span>
      </div>

      <ConversationPanel
        request={selectedTransaction.flagged as AskRequest}
        comments={selectedTransaction.flagged?.comments as AskRequestQueueComment[]}
        context="dataRowModal"
        transaction={selectedTransaction}
        onTransactionUpdate={handleTransactionUpdate}
        modalState={modalState}
        reportId={reportId || pendingFlag.reportId || undefined}
        messageLoading={isRefetching}
        handleOnCallback={handleComposerCallback}
        autoFocusComposer
      />
    </div>
  );
};

export default FlagCreationPanel;
